// Shared forged-stone button used by every in-realm prompt surface
// (EffectPromptDrawer for card-effect automation, CardActionMenu for
// the double-click action menu, and any future per-card decision UI).
//
// Style vocabulary matches the music library popover.
// AudioMixerPopover.IconAction + OPAQUE_TEXTURED_PANEL_STYLE. A full
// stone + chisel + scratches + cracks + noise texture stack over a
// fully opaque dark base, with layered multi-ring shadow that
// brightens on hover and recesses on press. Gold appears as accent
// only. Primary variant is gold-text + brighter ring; secondary is
// muted-gold-text + subtle ring.
//
// Canvas-relative dimensions are minimums. Text determines the final size;
// constrained labels wrap while retaining font-relative padding.

import { ACCENT_GOLD, CONTENT_BG_DEFAULT } from '../../lib/medievalTheme';

const DEFAULT_HEIGHT_CQW = 3;

const OPAQUE_BASE = '#0e0c08';

const STONE_BG_IDLE  = `linear-gradient(180deg, rgba(40,30,15,0.55) 0%, rgba(15,10,4,0.85) 100%), ${CONTENT_BG_DEFAULT}`;
const STONE_BG_HOVER = `linear-gradient(180deg, rgba(80,60,28,0.70) 0%, rgba(28,20,10,0.85) 100%), ${CONTENT_BG_DEFAULT}`;
const STONE_BG_PRESS = `linear-gradient(180deg, rgba(10,8,4,0.85) 0%, rgba(40,30,15,0.50) 100%), ${CONTENT_BG_DEFAULT}`;
const STONE_BLEND    = 'normal, soft-light, normal, normal, normal, normal';

const PRIMARY_TEXT   = ACCENT_GOLD;
const SECONDARY_TEXT = 'rgba(212, 168, 67, 0.78)';

const SHADOW_PRIMARY_IDLE = [
  'inset 0 0.0625cqw 0 rgba(255, 235, 195, 0.22)',
  'inset 0 -0.0625cqw 0 rgba(0, 0, 0, 0.45)',
  'inset 0 0 1.5cqw rgba(0, 0, 0, 0.4)',
  '0 0 0 0.0625cqw rgba(232, 195, 110, 0.78)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.6)',
  '0 0 0 0.25cqw rgba(212, 168, 67, 0.28)',
  '0 0.4cqw 1cqw rgba(0, 0, 0, 0.6)',
  '0 0 1.1cqw rgba(232, 195, 110, 0.18)',
].join(', ');
const SHADOW_PRIMARY_HOVER = [
  'inset 0 0.0625cqw 0 rgba(255, 240, 200, 0.32)',
  'inset 0 -0.0625cqw 0 rgba(0, 0, 0, 0.45)',
  'inset 0 0 1.75cqw rgba(120, 80, 25, 0.20)',
  '0 0 0 0.0625cqw rgba(255, 220, 130, 1)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.6)',
  '0 0 0 0.25cqw rgba(232, 195, 110, 0.52)',
  '0 0.5cqw 1.25cqw rgba(0, 0, 0, 0.65)',
  '0 0 2cqw rgba(232, 195, 110, 0.4)',
].join(', ');
const SHADOW_PRIMARY_PRESS = [
  'inset 0 0.125cqw 0.25cqw rgba(0, 0, 0, 0.55)',
  'inset 0 -0.0625cqw 0 rgba(255, 235, 195, 0.12)',
  'inset 0 0 2cqw rgba(0, 0, 0, 0.45)',
  '0 0 0 0.0625cqw rgba(212, 168, 67, 0.7)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.6)',
  '0 0 0 0.25cqw rgba(180, 140, 60, 0.22)',
  '0 0.15cqw 0.4cqw rgba(0, 0, 0, 0.55)',
].join(', ');

const SHADOW_SECONDARY_IDLE = [
  'inset 0 0.0625cqw 0 rgba(255, 235, 195, 0.14)',
  'inset 0 -0.0625cqw 0 rgba(0, 0, 0, 0.45)',
  'inset 0 0 1.5cqw rgba(0, 0, 0, 0.5)',
  '0 0 0 0.0625cqw rgba(180, 140, 60, 0.5)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.55)',
  '0 0 0 0.25cqw rgba(180, 140, 60, 0.16)',
  '0 0.35cqw 0.9cqw rgba(0, 0, 0, 0.55)',
].join(', ');
const SHADOW_SECONDARY_HOVER = [
  'inset 0 0.0625cqw 0 rgba(255, 235, 195, 0.22)',
  'inset 0 -0.0625cqw 0 rgba(0, 0, 0, 0.45)',
  'inset 0 0 1.75cqw rgba(0, 0, 0, 0.45)',
  '0 0 0 0.0625cqw rgba(212, 168, 67, 0.78)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.6)',
  '0 0 0 0.25cqw rgba(212, 168, 67, 0.30)',
  '0 0.45cqw 1.1cqw rgba(0, 0, 0, 0.6)',
  '0 0 1.4cqw rgba(212, 168, 67, 0.18)',
].join(', ');
const SHADOW_SECONDARY_PRESS = [
  'inset 0 0.125cqw 0.25cqw rgba(0, 0, 0, 0.55)',
  'inset 0 -0.0625cqw 0 rgba(255, 235, 195, 0.08)',
  'inset 0 0 2cqw rgba(0, 0, 0, 0.5)',
  '0 0 0 0.0625cqw rgba(180, 140, 60, 0.45)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.6)',
  '0 0 0 0.25cqw rgba(180, 140, 60, 0.13)',
  '0 0.15cqw 0.4cqw rgba(0, 0, 0, 0.55)',
].join(', ');

const TEXT_SHADOW_DEEP = '0 1px 2px rgba(0, 0, 0, 0.85), 0 0 0.6cqw rgba(212, 168, 67, 0.16)';

// Disabled state. NO transparency (the surrounding playmat shouldn't
// bleed through). Muted text color, dimmer rim shadow, identical
// opaque stone backdrop so the button reads as "present but inactive"
// rather than "fading away".
const DISABLED_TEXT  = 'rgba(150, 130, 90, 0.55)';
const SHADOW_DISABLED = [
  'inset 0 0.0625cqw 0 rgba(255, 235, 195, 0.08)',
  'inset 0 -0.0625cqw 0 rgba(0, 0, 0, 0.45)',
  'inset 0 0 1.5cqw rgba(0, 0, 0, 0.55)',
  '0 0 0 0.0625cqw rgba(110, 90, 50, 0.45)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.55)',
  '0 0 0 0.25cqw rgba(110, 90, 50, 0.12)',
  '0 0.25cqw 0.6cqw rgba(0, 0, 0, 0.5)',
].join(', ');

export default function StoneButton({
  children, onClick, variant = 'secondary', autoFocus = false,
  widthCqw, heightCqw = DEFAULT_HEIGHT_CQW,
  fontSizeCqw = 1.1,
  disabled = false,
}) {
  const isPrimary = variant === 'primary';
  const explicitWidth = typeof widthCqw === 'number';
  const idleStyle = {
    boxSizing: 'border-box',
    width: 'auto',
    minWidth: explicitWidth ? `min(${widthCqw}cqw, 100%)` : 0,
    maxWidth: '100%',
    minHeight: `${heightCqw}cqw`,
    height: 'auto',
    flexShrink: 0,
    padding: '0.4em 1em',
    fontSize: `${fontSizeCqw}cqw`,
    lineHeight: 1.35,
    fontFamily: 'inherit',
    fontWeight: 700,
    letterSpacing: '0.04em',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    backgroundColor: OPAQUE_BASE,
    backgroundImage: STONE_BG_IDLE,
    backgroundBlendMode: STONE_BLEND,
    color: disabled ? DISABLED_TEXT : (isPrimary ? PRIMARY_TEXT : SECONDARY_TEXT),
    boxShadow: disabled ? SHADOW_DISABLED : (isPrimary ? SHADOW_PRIMARY_IDLE : SHADOW_SECONDARY_IDLE),
    textShadow: TEXT_SHADOW_DEEP,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.6em',
    transform: 'scale(1)',
    transition: 'transform 80ms ease-out',
    // Disabled = no clicks, but the button stays fully opaque per
    // the v1.34 rule "no transparency on disabled state".
    pointerEvents: disabled ? 'none' : 'auto',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    textAlign: 'center',
  };

  const apply = (e, props) => Object.assign(e.currentTarget.style, props);
  const onEnter = (e) => { if (disabled) return; apply(e, {
    backgroundImage: STONE_BG_HOVER,
    boxShadow: isPrimary ? SHADOW_PRIMARY_HOVER : SHADOW_SECONDARY_HOVER,
    color: PRIMARY_TEXT,
    transform: 'scale(1.02)',
  }); };
  const onLeave = (e) => { if (disabled) return; apply(e, {
    backgroundImage: STONE_BG_IDLE,
    boxShadow: isPrimary ? SHADOW_PRIMARY_IDLE : SHADOW_SECONDARY_IDLE,
    color: isPrimary ? PRIMARY_TEXT : SECONDARY_TEXT,
    transform: 'scale(1)',
  }); };
  const onDown = (e) => { if (disabled) return; apply(e, {
    backgroundImage: STONE_BG_PRESS,
    boxShadow: isPrimary ? SHADOW_PRIMARY_PRESS : SHADOW_SECONDARY_PRESS,
    transform: 'scale(0.985)',
  }); };
  const onUp = (e) => { if (disabled) return; apply(e, {
    backgroundImage: STONE_BG_HOVER,
    boxShadow: isPrimary ? SHADOW_PRIMARY_HOVER : SHADOW_SECONDARY_HOVER,
    transform: 'scale(1.02)',
  }); };

  return (
    <button
      type="button"
      data-stone-button=""
      autoFocus={autoFocus}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onMouseDown={onDown}
      onMouseUp={onUp}
      style={idleStyle}
      disabled={disabled}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.6em', minWidth: 0, maxWidth: '100%' }}>
        {children}
      </span>
    </button>
  );
}
